import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { ImageUploader } from './ImageUploader';
import { Field } from './Field';

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Resolve the effective start/end dates, honoring legacy single `date` field. */
export const resolveSpecialRange = (special) => {
  if (!special) return { startDate: '', endDate: '' };
  const startDate = special.startDate || special.date || '';
  const endDate = special.endDate || special.date || startDate;
  return { startDate, endDate };
};

export const DailySpecialEditor = ({ special, onMeta, onItem, onAdd, onRemove, uploadPhoto, resolveImg, lang, translate }) => {
  const today = todayISO();
  const items = Array.isArray(special.items) ? special.items : [];
  const { startDate, endDate } = resolveSpecialRange(special);
  const inRange = startDate && endDate && today >= startDate && today <= endDate;
  const expired = special.active && endDate && today > endDate;
  const notYet = special.active && startDate && today < startDate;
  const invalidRange = startDate && endDate && startDate > endDate;

  // When user changes one date, store it in the new fields (and clear the legacy `date`)
  const setStartDate = (v) => { onMeta('startDate', v); onMeta('date', ''); };
  const setEndDate = (v) => { onMeta('endDate', v); onMeta('date', ''); };

  const translateItemField = async (idx, sourceField, targetField) => {
    try {
      const result = await translate(items[idx]?.[sourceField]?.it || '');
      onItem(idx, targetField, result);
    } catch (e) {
      alert('Traduzione fallita: ' + e.message);
    }
  };

  return (
    <section className="bg-white border-[1.5px] border-black rounded-[16px] p-5 md:p-6 max-w-4xl">
      <h2 className="font-display text-2xl md:text-3xl text-black mb-1">
        {lang === 'it' ? 'Piatti del giorno' : 'Daily specials'}
      </h2>
      <p className="text-xs text-black/60 mb-5">
        {lang === 'it'
          ? 'Aggiungi uno o più piatti (supplì, pinsa, dolce…). In home appariranno in un carosello scorrevole per tutti i giorni compresi fra "Da" e "A" (inclusi). A mezzanotte dell\'ultimo giorno spariscono.'
          : 'Add one or more specials (supplì, pinsa, dessert…). They appear on home in a scrollable carousel for every day in the "From"–"To" range (inclusive). Gone at midnight of the last day.'}
      </p>

      <div className="flex items-center justify-between gap-4 bg-black/[0.03] rounded-xl p-3 mb-4 flex-wrap">
        <div>
          <p className="font-bold text-black">
            {lang === 'it' ? 'Attivo' : 'Active'}
            {inRange && special.active && items.length > 0 && <span className="text-green-700"> — {lang === 'it' ? 'visibile oggi' : 'visible today'}</span>}
            {expired && <span className="text-amber-700"> — {lang === 'it' ? 'scaduto' : 'expired'}</span>}
            {notYet && <span className="text-blue-700"> — {lang === 'it' ? `inizia il ${startDate}` : `starts on ${startDate}`}</span>}
            {invalidRange && <span className="text-red-700"> — {lang === 'it' ? '"A" deve essere ≥ "Da"' : '"To" must be ≥ "From"'}</span>}
            {special.active && items.length === 0 && <span className="text-amber-700"> — {lang === 'it' ? 'vuoto, aggiungi almeno un piatto' : 'empty, add at least one dish'}</span>}
          </p>
          <p className="text-xs text-black/60">
            {lang === 'it' ? '"Attiva oggi" imposta Da e A alla data di oggi.' : '"Enable today" sets From and To to today.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          <button
            type="button"
            data-testid="daily-enable-today-btn"
            onClick={() => { setStartDate(today); setEndDate(today); onMeta('active', true); }}
            className="pill-btn primary text-sm"
          >
            {lang === 'it' ? 'Attiva oggi' : 'Enable today'}
          </button>
          <button
            type="button"
            data-testid="daily-disable-btn"
            onClick={() => onMeta('active', false)}
            className="pill-btn text-sm"
          >
            {lang === 'it' ? 'Disattiva' : 'Disable'}
          </button>
        </div>
      </div>

      <div className="flex items-end gap-4 bg-black/[0.03] rounded-xl p-3 mb-5 flex-wrap">
        <label className="flex flex-col text-xs">
          <span className="text-[10px] font-bold text-black/50 uppercase tracking-widest mb-1">{lang === 'it' ? 'Da' : 'From'}</span>
          <input
            type="date"
            data-testid="daily-start-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border-[1.5px] border-black/15 bg-white px-3 py-1.5 focus:outline-none focus:border-black text-sm"
          />
        </label>
        <label className="flex flex-col text-xs">
          <span className="text-[10px] font-bold text-black/50 uppercase tracking-widest mb-1">{lang === 'it' ? 'A' : 'To'}</span>
          <input
            type="date"
            data-testid="daily-end-date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border-[1.5px] border-black/15 bg-white px-3 py-1.5 focus:outline-none focus:border-black text-sm"
          />
        </label>
        {startDate && endDate && !invalidRange && (
          <p className="text-xs text-black/60 pb-2">
            {startDate === endDate
              ? (lang === 'it' ? `Solo il ${startDate}` : `Only on ${startDate}`)
              : (lang === 'it' ? `${startDate} → ${endDate}` : `${startDate} → ${endDate}`)}
          </p>
        )}
      </div>

      <ul className="space-y-5">
        {items.map((it, idx) => (
          <li key={it._id || idx} className="border-[1.5px] border-black/15 rounded-2xl p-4 md:p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display text-lg">{lang === 'it' ? `Piatto ${idx + 1}` : `Dish ${idx + 1}`}</p>
              <button type="button" onClick={() => onRemove(idx)} className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 font-bold text-xs">
                <Trash2 size={12} /> {lang === 'it' ? 'Rimuovi' : 'Remove'}
              </button>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <ImageUploader
                value={it.image}
                onChange={(v) => onItem(idx, 'image', v)}
                onUpload={(file) => uploadPhoto(file, (url) => onItem(idx, 'image', url))}
                resolveImg={resolveImg}
                t={() => ''}
              />
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label={`${lang === 'it' ? 'Nome' : 'Name'} IT`} value={it.name?.it || ''} onChange={(v) => onItem(idx, 'name_it', v)} />
                <Field
                  label={`${lang === 'it' ? 'Nome' : 'Name'} EN`}
                  value={it.name?.en || ''}
                  onChange={(v) => onItem(idx, 'name_en', v)}
                  onTranslate={translate ? () => translateItemField(idx, 'name', 'name_en') : undefined}
                  translateDisabled={!it.name?.it}
                />
                <Field label={`${lang === 'it' ? 'Descrizione' : 'Description'} IT`} value={it.description?.it || ''} onChange={(v) => onItem(idx, 'description_it', v)} textarea />
                <Field
                  label={`${lang === 'it' ? 'Descrizione' : 'Description'} EN`}
                  value={it.description?.en || ''}
                  onChange={(v) => onItem(idx, 'description_en', v)}
                  textarea
                  onTranslate={translate ? () => translateItemField(idx, 'description', 'description_en') : undefined}
                  translateDisabled={!it.description?.it}
                />
                <Field label={lang === 'it' ? 'Prezzo (€)' : 'Price (€)'} value={it.price || ''} onChange={(v) => onItem(idx, 'price', v)} />
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-4">
        <button type="button" onClick={onAdd} className="pill-btn">
          <Plus size={14} /> {lang === 'it' ? 'Aggiungi piatto del giorno' : 'Add daily special'}
        </button>
      </div>
    </section>
  );
};

export default DailySpecialEditor;
